import { createAction, createSlice } from '@reduxjs/toolkit';
import { getWorksheetBatchUpdateDefinition } from 'Components/WorksheetBatchUpdate';
import { getDefaultWorksheetStatusPatch } from 'Data/Helpers/worksheetHelpers';
import { FilterTestStatus } from 'Data/Search';
import { hasNonEmptyValue, hasValue } from 'Lib/Any';
import deepEquals from 'Lib/Object/deepEquals';
import copy from 'fast-clone';
import { pick, union } from 'ramda';
import * as oPath from '../../../Lib/Object/Path/Mutable';
import { getSyncTargetDefaults } from '../../Core/legacyDataSync';
import { applySyncPatch } from '../DataSync';
import { assembleQuery, hasImportMismatch } from './helpers';
import {
  ApplyUpdateAction,
  ApplyUpdateConflictAction,
  ApplyUpdateConflictResolveAction,
  ApplyUpdateResolveAction,
  CheckSelectedWorksheetsAction,
  CheckSelectedWorksheetsResolveAction,
  InitAction,
  PatchWorksheetStatusAction,
  RefreshWorksheetsResolveAction,
  SetAllSelectedAction,
  SetSelectedAction,
  SetUriParamsAction,
  SetValueAction,
  WorksheetBatchUpdateState,
} from './types';

const name = 'worksheetBatchUpdate';

const initialState: WorksheetBatchUpdateState = {
  typeId: undefined,
  worksheetCaption: undefined,
  worksheetType: undefined,
  fields: {},
  query: {
    enteredParams: {},
    uriParams: {},
  },
  worksheets: {},
  worksheetsState: {},
  updateData: {},
  updateFailures: {},
  status: {
    refreshingWorksheets: false,
    initialised: false,
    queryReady: false,
    applyingUpdate: false,
    checking: false,
    resolvingUpdateConflict: false,
    configuring: false,
  },
};

const slice = createSlice({
  name,
  initialState,
  reducers: {
    /**
     * Configures (or re-configures) the batch update page
     */
    configure: (state, action: InitAction) => {
      const {
        payload: { typeId, worksheetCaption, worksheetType, updateFields, selectedWorkOrder },
      } = action;

      // If we've changed the worksheet type, make sure we clear the currently loaded worksheets.
      if (state.typeId !== typeId) {
        state.worksheets = {};
        state.worksheetsState = {};
      }

      state.typeId = typeId;
      state.worksheetCaption = worksheetCaption;
      state.worksheetType = worksheetType;
      state.status.initialised = true;
      state.updateFailures = {};

      const defn = getWorksheetBatchUpdateDefinition(typeId);

      // Set up field configuration based on selected entry fields
      const fieldsToDisplay = union(
        updateFields,
        Object.values(pick(updateFields, defn.worksheetFields)).flatMap((e) => e.displayFieldsWhenEntered)
      );
      state.fields = Object.fromEntries(
        Object.keys(defn.worksheetFields).map((k) => [
          k,
          {
            update: updateFields.includes(k),
            display: fieldsToDisplay.includes(k),
          },
        ])
      );

      // Update entered params for query
      state.query.enteredParams = {
        // Default shared filters based on configuration
        // This is a "best-guess" at what the desired filter should be and should be overridden by any other information
        // (including existing values - so this is only applied based on the first configuration)

        ...{
          // If no update fields are selected, assume user intends to batch-check worksheets and default to Ready for Checking
          WorksheetStatus: hasNonEmptyValue(updateFields)
            ? FilterTestStatus.allStatuses
            : FilterTestStatus.readyForChecking,
        },
        // Take default values from definition or values supplied as uri params
        ...Object.fromEntries(
          Object.entries(
            pick(
              Object.keys(defn.queryFields), // Exclude params that are not enterable
              { ...defn.query.defaultParams, ...state.query.uriParams }
            )
          ).map(([k, v]) => [
            k,
            hasValue(defn.queryFields[k].paramToValue) ? defn.queryFields[k].paramToValue!(v as string) : v, // Apply conversion from query string to field value where required
          ])
        ),
        // Retain any entered values that are still enterable
        ...pick(Object.keys(defn.queryFields), state.query.enteredParams),
        // Overwrite entered values from the configuration dialog
        ...{
          ...(hasNonEmptyValue(selectedWorkOrder) && {
            WorkOrder: selectedWorkOrder,
          }),
        },
      };

      // Update update data
      state.updateData = {
        // Take default values from definition
        // Note this should include all update fields (not just those with defaults) so that empty fields will be cleared when this update data is applied.
        ...Object.fromEntries(
          Object.entries(pick(updateFields, defn.worksheetFields)).map(([k, v]) => [k, v.defaultValue])
        ),
        // Retain any existing update data that is still valid
        ...pick(updateFields, state.updateData),
        // Retain any related fields that may have been set by the update fields
        ...pick(
          Object.values(pick(updateFields, defn.worksheetFields)).flatMap((e) => e.alsoSets ?? []),
          state.updateData
        ),
      };
    },
    /**
     * Sets a value for the query (as entered by the user)
     */
    setQueryValue: (state, action: SetValueAction) => {
      const {
        payload: { name, value },
      } = action;

      if (value === undefined) delete state.query.enteredParams[name];
      else state.query.enteredParams[name] = value;
    },
    /**
     * Sets the query parameters passed in via the Web App uri
     */
    setUriParams: (state, action: SetUriParamsAction) => {
      const {
        payload: { uriParams },
      } = action;

      state.query.uriParams = uriParams;
    },
    /**
     * Assembles the current query based on supplied params
     */
    updateCurrentQuery: (state) => {
      state.query.currentQuery = assembleQuery(state.typeId!, state.query.uriParams, state.query.enteredParams);
      state.status.queryReady = state.query.currentQuery.isValid;
    },
    /**
     * Action that triggers a refresh of the worksheets loaded
     */
    refreshWorksheets: (state) => {
      state.status.refreshingWorksheets = true;
      state.updateFailures = {};
    },
    /**
     * Fired when worksheets have been successfully refreshed
     */
    refreshWorksheetsResolve: (state, action: RefreshWorksheetsResolveAction) => {
      const {
        payload: { worksheets, query },
      } = action;

      state.status.refreshingWorksheets = false;
      state.worksheets = Object.fromEntries(
        worksheets.map((w) => [
          w.id,
          {
            ...getSyncTargetDefaults(w),
            status: getDefaultWorksheetStatusPatch(),
          },
        ])
      );
      state.worksheetsState = Object.fromEntries(
        worksheets.map((w) => [
          w.id,
          // Persist state where worksheet was previously in result set
          state.worksheetsState[w.id] ?? {
            // Select new worksheets by default
            selected: true,
          },
        ])
      );
      state.query.lastQuery = query;
    },
    /**
     * Fired when worksheets could not be refreshed
     */
    refreshWorksheetsReject: (state) => {
      state.status.refreshingWorksheets = false;
    },
    /**
     * Set a value on the "update data" (the patch to apply to the batch of worksheets)
     */
    setUpdateDataValue: (state, action: SetValueAction) => {
      const {
        payload: { name, value },
      } = action;

      // We keep cleared values here so that they will be cleared when the update data value is applied
      state.updateData[name] = value;
    },
    /**
     * Action that triggers an update of all selected worksheets (applying the current update data)
     */
    applyUpdate: (state, _: ApplyUpdateAction) => {
      state.status.applyingUpdate = true;
      state.updateFailures = {};

      Object.entries(state.worksheets)
        .filter(([k, _]) => state.worksheetsState[k].selected)
        .forEach(([_, v]) => {
          oPath.setMultiple(v.syncModel, '', state.updateData, true);
        });
    },
    /**
     * Fired when worksheets have been successfully updated
     */
    applyUpdateResolve: (state, action: ApplyUpdateResolveAction) => {
      const {
        payload: { syncPatches },
      } = action;

      const defn = getWorksheetBatchUpdateDefinition(state.typeId!);

      state.status.applyingUpdate = false;

      Object.entries(syncPatches).forEach(([k, v]) => {
        let worksheet = state.worksheets[k];
        applySyncPatch(worksheet, v);

        const updateFailures: { [key: string]: true } = Object.fromEntries(
          Object.entries(action.payload.updateData)
            .filter(([k, v]) => {
              // Ignore fields that are not supported by batch update
              // This may include supplementary data we set alongside regular fields (e.g. associated test type)
              // It is assumed that if these fail, the (that is, at least currently true)
              if (!hasValue(defn.worksheetFields[k])) return false;

              const valueAfterUpdate = oPath.get(worksheet.syncModel, k);

              // Check if field has no value before and after (could be null or undefined)
              if (!hasValue(v) && !hasValue(valueAfterUpdate)) return false;

              // Compare values on update data with updated worksheets, and include anything that wasn't set as expected
              // Here we must consider differences between the submission model and the received model
              // That is difficult without having access to these models, so we just handle non-standard fields here
              // (it might make sense to allow the equality comparator to be overwritten in the field definition, but it isn't necessary yet)
              if (defn.worksheetFields[k]?.isImportSource) {
                return valueAfterUpdate?.Id !== v?.Id || hasImportMismatch(state.typeId!, worksheet.syncModel, k);
              }

              return !deepEquals(valueAfterUpdate, v);
            })
            .map(([k, _]) => [k, true])
        );

        if (hasNonEmptyValue(updateFailures)) state.updateFailures[k] = updateFailures;
      });
    },
    /**
     * Fired when an error occurs when updating worksheets
     */
    applyUpdateReject: (state) => {
      state.status.applyingUpdate = false;

      // Reset worksheets to last synced state
      Object.entries(state.worksheets).forEach(([k, v]) => {
        v.syncModel = v.syncModel_synced;
      });
    },
    /**
     * Action that indicates an update has some conflicting changes
     * (the user will be prompted to resolve these)
     */
    applyUpdateConflict: (state, action: ApplyUpdateConflictAction) => {
      const {
        payload: { syncPatches },
      } = action;

      state.status.resolvingUpdateConflict = true;

      Object.entries(syncPatches).forEach(([k, v]) => {
        let worksheet = state.worksheets[k];
        worksheet.syncModel_conflictClientBefore = v.serverModelSent;
        worksheet.syncModel_synced = v.serverModelReceived;
      });
    },
    /**
     * Fired when the strategy for resolving the conflict has been nominated
     */
    applyUpdateConflictResolve: (state, action: ApplyUpdateConflictResolveAction) => {
      const {
        payload: { choice },
      } = action;

      switch (choice) {
        case 'client':
          // Taking client changes will force the worksheet to the client's model of the worksheet,
          // reverting concurrent changes by other users. See QP-6419.
          // For the batch update page, we can workaround this by replacing the client model with the latest synced version of
          // the worksheet and reapplying the update data on top of this (as the user doesn't enter data on the worksheet directly).
          Object.values(state.worksheets).forEach((w) => {
            w.syncModel = copy(w.syncModel_synced);
          });
      }
      state.status.resolvingUpdateConflict = false;
    },
    /**
     * Set a single worksheet as selected or de-selected
     */
    setSelected: (state, action: SetSelectedAction) => {
      const {
        payload: { worksheetId, selected },
      } = action;

      state.worksheetsState[worksheetId].selected = selected;
    },
    /**
     * Set all worksheets as selected or de-selected
     */
    setAllSelected: (state, action: SetAllSelectedAction) => {
      const {
        payload: { selected },
      } = action;

      Object.entries(state.worksheetsState).forEach(
        ([k, v]) =>
          (v.selected =
            // Locked worksheets that cannot be unchecked are never selected
            !(state.worksheets[k].status.readonly.locked && !state.worksheets[k].status.readonly.uncheckAllowed) &&
            selected)
      );
    },
    /**
     * Update the "status" of each worksheet with the provided status patch
     */
    patchWorksheetStatus: (state, action: PatchWorksheetStatusAction) => {
      const {
        payload: { worksheetStatusPatches },
      } = action;
      worksheetStatusPatches.forEach((w) => {
        oPath.setMultiple(state.worksheets[w.worksheetId], 'status', w.status);
        // De-select worksheets if they have become readonly
        if (w.status.readonly.locked && !w.status.readonly.uncheckAllowed) {
          state.worksheetsState[w.worksheetId].selected = false;
        }
      });
    },
    /**
     * Reset batch update to the initial state
     */
    cleanup: (state) => {
      Object.assign(state, initialState);
    },

    /*
     * Action that triggers unchecking and updating of all selected worksheets
     */
    uncheckWorksheetsAndUpdate: (state) => {
      state.status.applyingUpdate = true;
    },
    /**
     * Action that updates the checked status of all selected worksheets
     */
    checkSelectedWorksheets: (state, _: CheckSelectedWorksheetsAction) => {
      state.status.checking = true;
    },
    /**
     * Fired when worksheets have been successfully checked
     */
    checkSelectedWorksheetsResolve: (state, action: CheckSelectedWorksheetsResolveAction) => {
      const {
        payload: { worksheetIds, checkedByPerson, checkedDate },
      } = action;

      // Apply checked by status to worksheets
      // Ideally we would sync the latest version of the worksheet.
      // However, at the moment the only way to do this is to (expensively) request each worksheet individually
      // as the checking endpoint only returns whether the request succeeded (and we can't batch query by uuid).
      // As the effects of this action are limited we can just optimistically apply the new checked status
      // to the client's version of these worksheets for now.
      // Any other side effects (via running the rules) can be resynced by refreshing worksheets or applying an update
      // (which does return the new version of the worksheets)
      worksheetIds.forEach((k) => {
        oPath.setMultiple(state.worksheets[k], 'syncModel.header', {
          CheckedDate: checkedDate,
          CheckedBy: checkedByPerson,
          IsChecked: true,
        });
        oPath.setMultiple(state.worksheets[k], 'syncModel_synced.header', {
          CheckedDate: checkedDate,
          CheckedBy: checkedByPerson,
          IsChecked: true,
        });
      });
      state.status.checking = false;
    },
    /**
     * Fired when an error occurs when checking worksheets
     */
    checkSelectedWorksheetsReject: (state) => {
      state.status.checking = false;
    },
  },
});

const { actions: sliceActions, reducer } = slice;
const actions = {
  ...sliceActions,
  /**
   * Action that is fired to trigger an update to the status of all loaded worksheets
   */
  updateAllWorksheetStatus: createAction(`${name}/updateAllWorksheetStatus`),
};
export { actions, reducer };
